import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  message,
} from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dashboardService } from '../services/api';
import { downloadCsv } from '../utils/csv';

const { RangePicker } = DatePicker;

const DEFAULT_PAGE_SIZE = 50;

const createDefaultDataState = () => ({
  resumo: {
    receita_total: 0,
    custo_total: 0,
    lucro_total: 0,
    margem_percentual: 0,
  },
  grafico: [],
  itens: [],
  paginacao: {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  },
});

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numeric);
};

const formatPercent = (value) => `${Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), 'pt-BR', { sensitivity: 'base' });

const compareNumber = (a, b) => Number(a || 0) - Number(b || 0);

const DashboardRentabilidade = () => {
  const [activeTab, setActiveTab] = useState('turma');
  const [periodo, setPeriodo] = useState([
    dayjs().subtract(5, 'month').startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [loadingByTab, setLoadingByTab] = useState({
    turma: false,
    modalidade: false,
    unidade: false,
    professor: false,
  });
  const [exportingByTab, setExportingByTab] = useState({
    turma: false,
    modalidade: false,
    unidade: false,
    professor: false,
  });
  const [dadosByTab, setDadosByTab] = useState({
    turma: createDefaultDataState(),
    modalidade: createDefaultDataState(),
    unidade: createDefaultDataState(),
    professor: createDefaultDataState(),
  });

  const paramsPeriodo = useMemo(() => {
    const [inicio, fim] = periodo || [];
    if (!inicio || !fim) {
      return null;
    }
    return {
      data_inicio: inicio.format('YYYY-MM-DD'),
      data_fim: fim.format('YYYY-MM-DD'),
    };
  }, [periodo]);

  const carregarRentabilidade = useCallback(
    async ({ dimensao, page = 1, pageSize = DEFAULT_PAGE_SIZE }) => {
      if (!paramsPeriodo) {
        message.warning('Selecione data de in�cio e fim para consultar.');
        return;
      }

      setLoadingByTab((prev) => ({ ...prev, [dimensao]: true }));
      try {
        const response = await dashboardService.getRentabilidade({
          ...paramsPeriodo,
          dimensao,
          page,
          page_size: pageSize,
          modo: 'completo',
        });
        const payload = response?.data || {};

        setDadosByTab((prev) => ({
          ...prev,
          [dimensao]: {
            resumo: payload.resumo || createDefaultDataState().resumo,
            grafico: Array.isArray(payload.grafico) ? payload.grafico : [],
            itens: Array.isArray(payload.itens) ? payload.itens : [],
            paginacao: payload.paginacao || {
              page,
              page_size: pageSize,
              total_items: 0,
              total_pages: 0,
              has_next: false,
              has_prev: false,
            },
          },
        }));
      } catch (error) {
        console.error(error);
        message.error('Erro ao carregar dashboard de rentabilidade.');
      } finally {
        setLoadingByTab((prev) => ({ ...prev, [dimensao]: false }));
      }
    },
    [paramsPeriodo]
  );

  useEffect(() => {
    carregarRentabilidade({ dimensao: activeTab, page: 1, pageSize: DEFAULT_PAGE_SIZE });
  }, [activeTab, carregarRentabilidade]);

  const aplicarAtalhoPeriodo = (tipo) => {
    let novoPeriodo;
    if (tipo === '3m') {
      novoPeriodo = [dayjs().subtract(2, 'month').startOf('month'), dayjs().endOf('month')];
    } else if (tipo === 'ano') {
      novoPeriodo = [dayjs().startOf('year'), dayjs().endOf('month')];
    } else {
      novoPeriodo = [dayjs().subtract(5, 'month').startOf('month'), dayjs().endOf('month')];
    }
    setPeriodo(novoPeriodo);
  };

  const handleAplicarFiltros = () => {
    carregarRentabilidade({ dimensao: activeTab, page: 1, pageSize: DEFAULT_PAGE_SIZE });
  };

  const handleExportCsv = async (dimensao) => {
    if (!paramsPeriodo) {
      message.warning('Selecione data de in�cio e fim para exportar.');
      return;
    }

    setExportingByTab((prev) => ({ ...prev, [dimensao]: true }));
    try {
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const response = await dashboardService.getRentabilidade({
          ...paramsPeriodo,
          dimensao,
          page,
          page_size: 200,
          modo: 'completo',
        });
        const payload = response?.data || {};
        const itens = Array.isArray(payload.itens) ? payload.itens : [];
        const paginacao = payload.paginacao || {};
        allRows.push(...itens);

        totalPages = Number(paginacao.total_pages || page);
        page += 1;
      }

      if (!allRows.length) {
        message.warning('Nenhum registro encontrado para exportar.');
        return;
      }

      const nomeDimensaoPorAba = {
        turma: 'turmas',
        modalidade: 'modalidades',
        unidade: 'unidades',
        professor: 'professores',
      };
      const nomeDimensao = nomeDimensaoPorAba[dimensao] || dimensao;
      downloadCsv(
        `rentabilidade_${nomeDimensao}_${paramsPeriodo.data_inicio}_a_${paramsPeriodo.data_fim}.csv`,
        ['competencia', 'entidade', 'receita', 'custo', 'lucro', 'margem_percentual'],
        allRows.map((item) => [
          item.competencia,
          item.entidade,
          Number(item.receita || 0).toFixed(2),
          Number(item.custo || 0).toFixed(2),
          Number(item.lucro || 0).toFixed(2),
          Number(item.margem_percentual || 0).toFixed(2),
        ])
      );

      message.success(`CSV exportado com ${allRows.length} registros.`);
    } catch (error) {
      console.error(error);
      message.error('Erro ao exportar CSV de rentabilidade.');
    } finally {
      setExportingByTab((prev) => ({ ...prev, [dimensao]: false }));
    }
  };

  const dataTab = dadosByTab[activeTab] || createDefaultDataState();
  const loading = loadingByTab[activeTab];

  const columns = useMemo(
    () => [
      {
        title: 'Compet�ncia',
        dataIndex: 'competencia',
        key: 'competencia',
        width: 130,
        sorter: (a, b) => compareText(a.competencia, b.competencia),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: activeTab === 'turma' ? 'Turma' : 'Modalidade',
        dataIndex: 'entidade',
        key: 'entidade',
        sorter: (a, b) => compareText(a.entidade, b.entidade),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Receita',
        dataIndex: 'receita',
        key: 'receita',
        width: 150,
        align: 'right',
        sorter: (a, b) => compareNumber(a.receita, b.receita),
        sortDirections: ['ascend', 'descend'],
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Custo Professores',
        dataIndex: 'custo',
        key: 'custo',
        width: 150,
        align: 'right',
        sorter: (a, b) => compareNumber(a.custo, b.custo),
        sortDirections: ['ascend', 'descend'],
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Lucro Operacional Parcial',
        dataIndex: 'lucro',
        key: 'lucro',
        width: 150,
        align: 'right',
        sorter: (a, b) => compareNumber(a.lucro, b.lucro),
        sortDirections: ['ascend', 'descend'],
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Margem %',
        dataIndex: 'margem_percentual',
        key: 'margem_percentual',
        width: 130,
        align: 'right',
        sorter: (a, b) => compareNumber(a.margem_percentual, b.margem_percentual),
        sortDirections: ['ascend', 'descend'],
        render: (value) => formatPercent(value),
      },
    ],
    [activeTab]
  );

  const entidadeLabelByTab = {
    turma: 'Turma',
    modalidade: 'Modalidade',
    unidade: 'Unidade',
    professor: 'Professor',
  };

  columns[1].title = entidadeLabelByTab[activeTab] || 'Entidade';

  return (
    <div className="dashboard-page" style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard de Rentabilidade</h1>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Hist�rico operacional por turma, modalidade, unidade e professor
          </p>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} lg={10}>
            <RangePicker
              value={periodo}
              onChange={(dates) => setPeriodo(dates)}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              allowClear={false}
            />
          </Col>
          <Col>
            <Button icon={<SearchOutlined />} type="primary" onClick={handleAplicarFiltros}>
              Aplicar
            </Button>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => aplicarAtalhoPeriodo('3m')}>�ltimos 3 meses</Button>
              <Button onClick={() => aplicarAtalhoPeriodo('6m')}>�ltimos 6 meses</Button>
              <Button onClick={() => aplicarAtalhoPeriodo('ano')}>Ano atual</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={(value) => setActiveTab(value)}
        items={[
          { key: 'turma', label: 'Turmas' },
          { key: 'modalidade', label: 'Modalidades' },
          { key: 'unidade', label: 'Unidades' },
          { key: 'professor', label: 'Professores' },
        ]}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Receita Total" value={dataTab.resumo.receita_total} formatter={formatCurrency} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Custo Professores" value={dataTab.resumo.custo_total} formatter={formatCurrency} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Lucro Operacional Parcial" value={dataTab.resumo.lucro_total} formatter={formatCurrency} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Margem %" value={dataTab.resumo.margem_percentual} formatter={formatPercent} />
          </Card>
        </Col>
      </Row>

      <Card title="Evolu��o Mensal" style={{ marginBottom: 16 }}>
        {loading ? (
          <Spin />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={dataTab.grafico}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="competencia" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="receita" name="Receita" fill="#1677ff" />
              <Bar dataKey="custo" name="Custo Professores" fill="#faad14" />
              <Bar dataKey="lucro" name="Lucro Operacional Parcial" fill="#52c41a" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card
        title="Tabela de Rentabilidade"
        extra={
          <Button
            icon={<DownloadOutlined />}
            loading={exportingByTab[activeTab]}
            onClick={() => handleExportCsv(activeTab)}
          >
            Exportar CSV
          </Button>
        }
      >
        <Table
          rowKey={(row) => `${row.competencia}-${row.entidade}`}
          columns={columns}
          dataSource={dataTab.itens}
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: dataTab.paginacao.page,
            pageSize: dataTab.paginacao.page_size,
            total: dataTab.paginacao.total_items,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
          }}
          onChange={(pagination, _filters, _sorter, extra) => {
            if (extra?.action !== 'paginate') {
              return;
            }

            carregarRentabilidade({
              dimensao: activeTab,
              page: pagination.current,
              pageSize: pagination.pageSize,
            });
          }}
        />
      </Card>
    </div>
  );
};

export default DashboardRentabilidade;
